import { Box, Drawer, Typography, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid2';
import Input from 'components/Common/Input';
import { useForm } from 'react-hook-form';
import Button from 'components/Common/Button';
import { useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { agencyTeamMemberSchema } from 'utils/schema/validator/TeamMemberValidation';
import { useCreateOrUpdateAgencyTeam, useGetAgencyTeam } from 'apis/agency/team/hook';
import TelInput from 'components/Common/TelInput';
import { IoClose } from 'react-icons/io5';
import { TeamMemberContentSkeleton, TeamMemberFooterSkeleton } from './TeamMemberFormSkeleton';
import Select from '@/components/Common/Select';
import { TeamMemberRole } from '@/apis/agency/team/types';

export default function TeamForm() {
  const { teamId } = useParams();
  const navigate = useNavigate();

  const { data: teamMember, isLoading } = useGetAgencyTeam();

  const { handleSubmit, control } = useForm({
    resolver: zodResolver(agencyTeamMemberSchema),
    mode: 'onBlur',
    values: teamId ? { team_member: teamMember } : { team_member: { role: TeamMemberRole.AgentBuilder } }
  });

  const { mutate: handleTeamMember, isPending } = useCreateOrUpdateAgencyTeam();

  const onSubmit = (data?: any) => {
    handleTeamMember(data);
  };

  return (
    <Drawer
      anchor='right'
      open={true}
      onClose={() => navigate(-1)}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400, md: 600 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 100001
        }
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid #DEDEDE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Typography variant='h6'>{teamId ? 'Edit' : 'Add'} Member</Typography>
        <IconButton onClick={() => navigate(-1)} size='small'>
          <IoClose size={24} />
        </IconButton>
      </Box>

      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3
          }}
        >
          {isLoading ? (
            <TeamMemberContentSkeleton />
          ) : (
            <Grid container spacing='26px'>
              <Grid size={12}>
                <Input name='team_member.name' label='Name' control={control} fullWidth required />
              </Grid>
              <Grid size={12}>
                <Input name='team_member.email' label='Email' control={control} fullWidth required />
              </Grid>
              <Grid size={12}>
                <TelInput
                  name='team_member.phone_number'
                  label='Mobile Number'
                  control={control}
                  defaultCountry='US'
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <Select
                  name='team_member.role'
                  label='Role'
                  control={control}
                  options={[
                    { value: TeamMemberRole.AgencyAdmin, label: 'Agency Admin' },
                    { value: TeamMemberRole.AgentBuilder, label: 'Agent Builder' }
                  ]}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
          )}
        </Box>

        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid #DEDEDE',
            display: 'flex',
            justifyContent: 'end',
            gap: '16px',
            flexShrink: 0
          }}
        >
          {isLoading ? (
            <TeamMemberFooterSkeleton />
          ) : (
            <>
              <Button
                color='secondary'
                variant='outlined'
                sx={{ border: '1px solid #F3F3F3' }}
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button type='submit' variant='contained' isPending={isPending}>
                {teamId ? 'Save' : 'Add'}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
